<template>
    <div class="ui-popup">
        <div
            class="ui-popup__wrapper"
            ref="popup"
        >
            <div
                v-if="$slots.activator"
                ref="activator"
                class="ui-popup__activator"
            >
                <slot name="activator" />
            </div>
            <UiTeleport to="#app">
                <transition :name="`ui-popup-transition_${transition}`" :css="!!transition">
                    <div
                        v-if="localValue"
                        ref="overlay"
                        class="ui-popup-overlay"
                    >
                        <div
                            ref="content"
                            class="ui-popup-content"
                        >
                            <slot />
                        </div>
                        <div
                            v-if="$slots.arrow"
                            ref="arrow"
                            class="ui-popup-arrow"
                        >
                            <slot name="arrow" />
                        </div>
                    </div>
                </transition>
            </UiTeleport>
        </div>
    </div>
</template>
<script>
import UiTeleport from './UiTeleport.vue';
import { watch, ref, onBeforeUnmount, reactive, computed } from 'vue';
import { useWindowSize } from '../../assets/js/vue/utlis/useWindowSize';
import { useElRect } from '../../assets/js/vue/utlis/useElRect';
import { useScroll } from '../../assets/js/vue/utlis/useScroll';
import { useResizeObserver } from '../../assets/js/vue/utlis/useResizeObserver';
import fastdom from 'fastdom';
import isEqual from 'lodash/isEqual';
import { useStore } from '../../store';
import { useEventListener } from '@vueuse/core';

export default {
    components: {
        UiTeleport,
    },
    props: {
        /**
         * показать/скрыть попап
         */
        value: Boolean,
        /**
         * к какому краю активатора выравнивается попап
         */
        align: {
            type: String,
            default: 'start',
            validator: (val) => ['start', 'end', 'center'].includes(val),
        },
        /**
         * с какой стороны активатора расположен попап
         * при выборе top или bottom - он перемещается на противаположную, если не влазит в экран
         */
        placement: {
            type: String,
            default: 'top',
            validator: (val) => ['top', 'bottom', 'left', 'right'].includes(val),
        },
        /**
         * смещение от края, по которому попап выравнен. число в пикселях
         */
        alignOffset: {
            type: Number,
            default: 0,
        },
        /**
         * тип анимации появления и скрытия
         */
        transition: {
            type: String,
            default: 'fade',
            validator: (val) => ['fade'].includes(val),
        },
    },
    setup(props, { emit }) {
        const localValue = ref(false);
        const overlay = ref(null);

        const containerPadding = ref(40);


        /**
         * POSITION LOGIC
         */

        const contentStyle = ref({
            top: 'unset',
            bottom: 'unset',
            left: 'unset',
            right: 'unset',
        });

        const arrowStyle = ref({
            top: 'unset',
            bottom: 'unset',
            left: 'unset',
            right: 'unset',
            rotate: 'unset',
            translate: 'unset',
            transformOrigin: 'unset',
        });

        const content = ref(null);
        const popup = ref(null);
        const arrow = ref(null);

        const popupRect = useElRect(popup);
        const contentRect = reactive({
            width: 0,
            height: 0,
        });
        useResizeObserver(content, (entries) => {
            entries.forEach((entry) => {
                if (entry.borderBoxSize?.[0]) { // supported ios >=15.4
                    const { blockSize, inlineSize } = entry.borderBoxSize[0];
                    contentRect.height = blockSize;
                    contentRect.width = inlineSize;
                } else {
                    fastdom.measure(() => {
                        const { width, height } = entry.target.getBoundingClientRect();
                        contentRect.height = height;
                        contentRect.width = width;
                    });
                }
            });
        }, { box: 'border-box' });
        const windowSize = useWindowSize({ includeScrollbar: false });
        const { y: scrollY, x: scrollX } = useScroll();


        const store = useStore();
        const scrollHeight = computed(() => store.state.app.pageHeight);
        const scrollWidth = computed(() => store.state.app.pageWidth);
        const arrowRect = reactive({
            width: 0,
            height: 0,
        });
        useResizeObserver(arrow, (entries) => {
            entries.forEach((entry) => {
                if (entry.borderBoxSize?.[0]) {
                    const { blockSize, inlineSize } = entry.borderBoxSize[0];
                    arrowRect.height = blockSize;
                    arrowRect.width = inlineSize;
                } else {
                    fastdom.measure(() => {
                        const { width, height } = entry.target.getBoundingClientRect();
                        arrowRect.height = height;
                        arrowRect.width = width;
                    });
                }
            });
        }, { box: 'border-box' });

        const updateStyleState = () => {

            let initContentRect = {};
            if (!contentRect.width || !contentRect.height) {
                initContentRect = content.value?.getBoundingClientRect();
            }
            const contentWidth = contentRect.width || initContentRect?.width;
            const contentHeight = contentRect.height || initContentRect?.height;

            const contentStyleTemp = {};
            const arrowStyleTemp = {};

            // x
            if (props.placement === 'left') {
                contentStyleTemp.right = `${scrollWidth.value - scrollX.value - popupRect.left.value + arrowRect.width}px`;
                contentStyleTemp.left = 'unset';
                arrowStyleTemp.left = `${popupRect.left.value - arrowRect.width * 2}px`;
                arrowStyleTemp.right = 'unset';
                arrowStyleTemp.rotate = '-90deg';
                arrowStyleTemp.transformOrigin = 'right top';
            } else if (props.placement === 'right') {
                contentStyleTemp.left = `${popupRect.right.value + arrowRect.width}px`;
                contentStyleTemp.right = 'unset';
                arrowStyleTemp.left = `${popupRect.right.value + arrowRect.width}px`;
                arrowStyleTemp.right = 'unset';
                arrowStyleTemp.rotate = '90deg';
                arrowStyleTemp.transformOrigin = 'left top';
            } else if (props.align === 'start') {
                contentStyleTemp.left = `${popupRect.left.value + props.alignOffset}px`;
                contentStyleTemp.right = 'unset';
                arrowStyleTemp.left = `${popupRect.left.value - Math.max((arrowRect.width - popupRect.width.value) / 2, 0)}px`;
                arrowStyleTemp.right = 'unset';
            } else if (props.align === 'end') {
                contentStyleTemp.right = `${Math.min(windowSize.width.value - popupRect.right.value + props.alignOffset, windowSize.width.value - containerPadding.value - contentWidth)}px`;
                contentStyleTemp.left = 'unset';
                arrowStyleTemp.left = `${popupRect.right.value - arrowRect.width + Math.max((arrowRect.width - popupRect.width.value) / 2, 0)}px`;
                arrowStyleTemp.right = 'unset';
            } else if (props.align === 'center') {
                contentStyleTemp.left = `${popupRect.left.value + popupRect.width.value / 2 - contentWidth / 2 + props.alignOffset}px`;
                contentStyleTemp.right = 'unset';
                arrowStyleTemp.left = `${popupRect.left.value + popupRect.width.value / 2 - arrowRect.width / 2}px`;
                arrowStyleTemp.right = 'unset';
            }

            // y
            if (props.placement === 'top' && (popupRect.top.value - contentHeight) >= 0 || props.placement === 'bottom' && (popupRect.bottom.value + contentHeight) > windowSize.height.value) {
                contentStyleTemp.bottom = `${scrollHeight.value - scrollY.value - popupRect.top.value + arrowRect.height}px`;
                contentStyleTemp.top = 'unset';
                arrowStyleTemp.bottom = `${scrollHeight.value - scrollY.value - popupRect.top.value}px`;
                arrowStyleTemp.top = 'unset';
                arrowStyleTemp.rotate = 'unset';
                arrowStyleTemp.translate = 'unset';
            } else if (props.placement === 'bottom' && (popupRect.bottom.value + contentHeight) <= windowSize.height.value || props.placement === 'top' && (popupRect.top.value - contentHeight) < 0) {
                contentStyleTemp.top = `${scrollY.value + popupRect.bottom.value + arrowRect.height}px`;
                contentStyleTemp.bottom = 'unset';
                arrowStyleTemp.top = `${scrollY.value + popupRect.bottom.value}px`;
                arrowStyleTemp.bottom = 'unset';
                arrowStyleTemp.rotate = '180deg';
                arrowStyleTemp.translate = 'unset';
            } else if (props.align === 'start') {
                contentStyleTemp.top = `${scrollY.value + popupRect.top.value + props.alignOffset}px`;
                contentStyleTemp.bottom = 'unset';
                arrowStyleTemp.top = `${scrollY.value + popupRect.top.value - Math.max((arrowRect.height - popupRect.height.value) / 2, 0)}px`;
                arrowStyleTemp.bottom = 'unset';
            } else if (props.align === 'end') {
                contentStyleTemp.bottom = `${scrollHeight.value - scrollY.value - popupRect.bottom.value + props.alignOffset}px`;
                contentStyleTemp.top = 'unset';
                arrowStyleTemp.top = `${scrollY.value + popupRect.bottom.value - arrowRect.height + Math.max((arrowRect.height - popupRect.height.value) / 2, 0)}px`;
                arrowStyleTemp.bottom = 'unset';
            } else if (props.align === 'center') {
                contentStyleTemp.top = `${scrollY.value + popupRect.top.value - contentHeight / 2 + popupRect.height.value / 2}px`;
                contentStyleTemp.bottom = 'unset';
                arrowStyleTemp.top = `${scrollY.value + popupRect.top.value - (arrowRect.width - popupRect.height.value) / 2}px`;
                arrowStyleTemp.bottom = 'unset';
            }

            if (!isEqual(contentStyle.value, contentStyleTemp)) contentStyle.value = contentStyleTemp;
            if (!isEqual(arrowStyle.value, arrowStyleTemp)) arrowStyle.value = arrowStyleTemp;
        };

        watch(localValue, (val) => {
            if (!val) return;

            popupRect.update();
            updateStyleState();

        }, { immediate: true, flush: 'sync' });

        watch([
            popupRect.right, popupRect.left, popupRect.bottom, popupRect.top, popupRect.width,
            windowSize.width, windowSize.height, scrollY, scrollX, scrollHeight, scrollWidth,
            localValue, () => contentRect.width, () => contentRect.height, () => arrowRect.height, () => arrowRect.width,
        ], () => {
            if (!localValue.value) return;

            updateStyleState();

        }, { immediate: true, flush: 'sync' });

        watch([contentStyle, content], ([{ top, bottom, left, right }, contentEl]) => {
            if (!(contentEl instanceof HTMLElement)) return;

            fastdom.mutate(() => {
                contentEl.style.top = top;
                contentEl.style.bottom = bottom;
                contentEl.style.left = left;
                contentEl.style.right = right;
            });
        }, { immediate: true, deep: true, flush: 'sync' });

        watch([arrowStyle, arrow], ([{ top, bottom, left, right, rotate, translate, transformOrigin, }, arrowEl]) => {
            if (!(arrowEl instanceof HTMLElement)) return;

            fastdom.mutate(() => {
                arrowEl.style.top = top;
                arrowEl.style.bottom = bottom;
                arrowEl.style.left = left;
                arrowEl.style.right = right;
                arrowEl.style.rotate = rotate;
                arrowEl.style.translate = translate;
                arrowEl.style.transformOrigin = transformOrigin;
            });
        }, { immediate: true, deep: true, flush: 'sync' });

        /**
         * POSITION LOGIC END
         */

        /**
         * STATE LOGIC START
         */

        const isHovered = ref(false);

        const onMouseOverOut = (e) => {
            if (!localValue.value) return;

            if (!overlay.value?.contains(e.target) && overlay.value !== e.target) return;

            isHovered.value = e.type === 'mouseover';
        };

        useEventListener(overlay, 'mouseover', onMouseOverOut, { passive: true });
        useEventListener(overlay, 'mouseout', onMouseOverOut, { passive: true });

        watch(isHovered, (val) => {
            emit('update:is-hovered', val);
        });

        onBeforeUnmount(() => {
            isHovered.value = false;
        })

        /**
         * STATE LOGIC END
         */

        return {
            contentStyle,
            localValue,
            popupRect,
            contentRect,
            windowSize,
            content,
            popup,
            scrollY,
            scrollHeight,
            scrollWidth,
            onMouseOverOut,
            isHovered,
            overlay,
            arrow,
            arrowRect,
            arrowStyle,
        };
    },
    data() {
        return {
            target: 'body',
            pointerDownInside: false,
        };
    },
    watch: {
        value: {
            immediate: true,
            handler(val) {
                this.localValue = val;
            },
        },
        localValue(val) {
            if (this.value !== val) this.$emit('input', val);
        },
    },
    methods: {
        onClickOutside() {
            this.$emit('click-outside');
        },
        onClickHndle(e) {
            if (!this.localValue) return;

            const { target, type } = e;

            const downInside = this.$refs.content?.contains(target) || this.$refs.activator?.contains(target);

            if (type === 'pointerdown') this.pointerDownInside = downInside;

            if (type === 'pointerdown' || downInside) return;

            if (!this.pointerDownInside) this.onClickOutside();
        },
    },
    mounted() {
        window.addEventListener('click', this.onClickHndle, { passive: true });
        window.addEventListener('pointerdown', this.onClickHndle, { passive: true });
        window.addEventListener('focusin', this.onClickHndle, { passive: true });
    },
    unmounted() {
        window.removeEventListener('click', this.onClickHndle, { passive: true });
        window.removeEventListener('pointerdown', this.onClickHndle, { passive: true });
        window.removeEventListener('focusin', this.onClickHndle, { passive: true });
    },
};
</script>
<style lang="scss">
.ui-popup {
    display: inherit;

    &__wrapper {
        display: block;
    }
}

.ui-popup-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    pointer-events: none;
    // поставить на 2000 после правки z-index модалки, у которой 10000 =)
    z-index: 102000;
}

.ui-popup-content {
    pointer-events: all;
    position: absolute;
}

.ui-popup-arrow {
    position: absolute;
}

.ui-popup-transition {
    &_fade {
        &-enter-active,
        &-leave-active {
            transition: opacity .3s;
        }

        &-enter,
        &-leave-to {
            opacity: 0;
        }
    }
}
</style>
